import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import DiscoverMoreTiles from 'features/discoverMore/components/DiscoverMoreTiles';
import useDiscoverMore from 'features/discoverMore/hooks/useDiscoverMore';
import React, { Fragment, useMemo } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import {
  parseGQLAnalyticsEvent,
  parseOptionalGQLAnalyticsEvent,
} from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDefaultError from 'shared/hooks/errorFormatters/useDefaultError/useDefaultError';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import theme from 'shared/styles/theme';

// Map item IDs to navigation routes
const ITEM_ROUTES = {
  SAVINGS_ACCOUNT: '/open-savings',
  BELIEVE_CARD: '/believe/apply',
  DIRECT_DEPOSIT: '/direct-deposit-benefits',
  VARO_ADVANCE: '/varo-advance/link-account/intro',
  LINE_OF_CREDIT: '/varo-line-of-credit/line-of-credit-entry-point',
  PAY_OVER_TIME: '/pay-over-time/landing-page',
} as const;

const DiscoverMoreScreen = () => {
  const router = useRouter();
  const { discoverMoreData, fetching, hasError } = useDiscoverMore();
  const { segmentClient } = useAnalyticsConfiguration();
  const defaultError = useDefaultError();

  const displayAnalytics = parseOptionalGQLAnalyticsEvent(
    discoverMoreData.displayAnalytics ?? null,
  );
  useViewDidFocusLogScreenAnalytics(displayAnalytics);

  // useMemo to avoid recalculating options on every render
  const { title, sections } = useMemo(() => {
    return {
      ...discoverMoreData,
      sections: discoverMoreData.sections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({
          ...item,
          onPress: () => {
            if (item.onClickAnalytics) {
              const { name, properties } = parseGQLAnalyticsEvent(
                item.onClickAnalytics,
              );
              void segmentClient.logEvent(name, properties);
            }
            const route = ITEM_ROUTES[item.keyId ?? ''];
            if (route) {
              router.push({
                pathname: route,
                params: { from: 'discover-more' },
              });
            }
          },
        })),
      })),
    };
  }, [discoverMoreData, segmentClient, router]);

  const isLoading = fetching && sections.length === 0;

  if (hasError && sections.length === 0) {
    return (
      <ErrorScreen
        screenType={ErrorScreenType.Error}
        messageHeadline={defaultError.title}
        messageBody={defaultError.message}
        primaryButton={{
          title: 'Go back',
          onPress: () => navigateBack(),
        }}
      />
    );
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={() => navigateBack()}
            />
          ),
        }}
      />
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <LoadingSpinner />
        </View>
      ) : (
        <ScrollView style={styles.scrollViewContainer}>
          <View style={styles.headerTitleContainer}>
            <TitleLgText numberOfLines={1}>{title}</TitleLgText>
          </View>
          <View style={styles.sectionsContainer}>
            {sections.map((section, index) => (
              <Fragment key={section.id || section.sectionLabel}>
                <View style={styles.sectionBlock}>
                  <DiscoverMoreTiles
                    sectionLabel={section.sectionLabel}
                    items={section.items}
                  />
                </View>
                {index < sections.length - 1 ? (
                  <View style={styles.pageDivider} />
                ) : null}
              </Fragment>
            ))}
          </View>
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollViewContainer: {
    flex: 1,
  },
  headerTitleContainer: {
    paddingTop: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    paddingBottom: theme.spacing.s,
  },
  sectionsContainer: {
    gap: theme.spacing.s,
  },
  sectionBlock: {
    backgroundColor: theme.color.background.primary,
  },
  pageDivider: {
    backgroundColor: theme.color.background.surface,
    height: 8,
  },
});

export default DiscoverMoreScreen;
